import {
	VALIDITY_STATUS,
	daysUntil,
	getValidityStatus,
	isKnownInterval,
	toServerCalendarDate,
} from './ExpiryRule';

// The server's calendar day of a datetime, shared with the maintenance rule (ExpiryRule).
export { toServerCalendarDate };

// rest-api status vocabulary (AddonDomainMapper.determine_product_addon_status). 'available' covers a
// grant that never started (start on activation) and an expired trial; 'pending active' has not started yet.
export const ADDON_SERVER_STATUS = Object.freeze({
	TRIAL: 'trial',
	ACTIVE: 'active',
	PENDING_ACTIVE: 'pending active',
	EXPIRED: 'expired',
	AVAILABLE: 'available',
});

// What a grant is for (SYN-3445): a trial of the license, or the license itself.
export const ADDON_KIND = Object.freeze({
	TRIAL: 'trial',
	LICENSE: 'license',
});

// What a license row of the pane is in: the validity of a licensed grant, or one of two states for a grant that has
// not started. Only the validity states count for the machine list's alert (getAddonsAlert).
export const ADDON_ROW_STATUS = Object.freeze({
	...VALIDITY_STATUS,
	SCHEDULED: 'scheduled',
	STARTS_ON_ACTIVATION: 'startsOnActivation',
});

// A grant that is licensed now, or was and lapsed.
const LICENSED_ADDON_STATUSES = Object.freeze([
	ADDON_SERVER_STATUS.TRIAL,
	ADDON_SERVER_STATUS.ACTIVE,
	ADDON_SERVER_STATUS.EXPIRED,
]);

const STATUS_RANK = Object.freeze({
	[ADDON_ROW_STATUS.EXPIRED]: 0,
	[ADDON_ROW_STATUS.EXPIRING]: 1,
	[ADDON_ROW_STATUS.ACTIVE]: 2,
	[ADDON_ROW_STATUS.SCHEDULED]: 3,
	[ADDON_ROW_STATUS.STARTS_ON_ACTIVATION]: 4,
});

export const UNLIMITED_TIER_LABEL = 'Unlimited';

// A plain count carries no symbol on screen: "12", not "12 count". Every other unit follows its amount.
const COUNT_UNIT_SYMBOL = 'count';

// The tier shown next to the license name, or null for a license without one (MFA) and for an older rest-api that
// sends no tier. Unlimited comes from the isUnlimited flag only: the stored tier value (-1) is never shown. A tier
// value is in base units (12 TB arrives as 12000000000000); the unit says how to read it, and a tier without a unit
// is shown as is.
export const formatAddonTier = (productAddon) => {
	if (productAddon.isUnlimited) {
		return UNLIMITED_TIER_LABEL;
	}

	const tier = productAddon.tier;

	if (!tier || !Number.isFinite(tier.value)) {
		return null;
	}

	const unit = tier.unit;

	if (!unit) {
		return String(tier.value);
	}

	const factor = Number.isFinite(unit.multiplicationFactor) && unit.multiplicationFactor > 0 ? unit.multiplicationFactor : 1;
	// toPrecision trims the float noise of the division (4.35 TB must not read 4.3500000000000005 TB).
	const amount = Number((tier.value / factor).toPrecision(12));

	return unit.symbol && unit.symbol !== COUNT_UNIT_SYMBOL ? `${amount} ${unit.symbol}` : String(amount);
};

// A grant that never ends is a started grant with no end date: the batch has no forever flag (D144), and a
// follows-maintenance grant with no maintenance is left out of the batch, so no other row lacks an end date.
const isForeverGrant = (productAddon) => {
	return Boolean(productAddon.startDate) && !productAddon.endDate;
};

// The status and date of a license row, or null for a grant the pane does not list: an expired trial
// ('available' with a start date), and a grant with neither date that does not start on activation. The date
// is the end date, except for a scheduled grant, whose start date is what the user waits for.
export const getAddonRowStatus = (productAddon, interval) => {
	if (productAddon.status === ADDON_SERVER_STATUS.PENDING_ACTIVE) {
		return { status: ADDON_ROW_STATUS.SCHEDULED, calendarDate: toServerCalendarDate(productAddon.startDate) };
	}

	if (productAddon.status === ADDON_SERVER_STATUS.AVAILABLE) {
		return productAddon.startOnActivation && !productAddon.startDate
			? { status: ADDON_ROW_STATUS.STARTS_ON_ACTIVATION, calendarDate: null }
			: null;
	}

	if (!LICENSED_ADDON_STATUSES.includes(productAddon.status)) {
		return null;
	}

	if (productAddon.status === ADDON_SERVER_STATUS.EXPIRED) {
		const calendarDate = toServerCalendarDate(productAddon.endDate);

		return calendarDate ? { status: ADDON_ROW_STATUS.EXPIRED, calendarDate } : null;
	}

	const calendarDate = toServerCalendarDate(productAddon.endDate);

	if (!calendarDate) {
		return isForeverGrant(productAddon) ? { status: ADDON_ROW_STATUS.ACTIVE, calendarDate: null } : null;
	}

	// The server called it active, so a browser clock a day ahead must not turn it into Expired.
	const daysLeft = Math.max(daysUntil(calendarDate), 0);

	return { status: getValidityStatus(daysLeft, interval), calendarDate };
};

// Rows without a date (forever, starts on activation) go after the dated ones of the same status.
const compareCalendarDates = (dateA, dateB) => {
	if (dateA === dateB) {
		return 0;
	}

	if (!dateA || !dateB) {
		return dateA ? -1 : 1;
	}

	return dateA.localeCompare(dateB);
};

// null means "unknown": the field is missing (older rest-api), not an array, or the company interval has not
// resolved yet. An empty array is a known "no licenses". Each row is the grant Central picked for its license
// (GET /licensing/products/batch), so the pane never groups or picks anything itself.
export const getAddonRows = (productAddons, interval) => {
	if (!Array.isArray(productAddons) || !isKnownInterval(interval)) {
		return null;
	}

	return productAddons
		.map((productAddon) => {
			const rowStatus = getAddonRowStatus(productAddon, interval);

			if (!rowStatus) {
				return null;
			}

			return {
				id: productAddon.id,
				name: productAddon.name,
				tier: formatAddonTier(productAddon),
				isForever: isForeverGrant(productAddon),
				isTemporary: Boolean(productAddon.isTemporary),
				isTrial: productAddon.kind === ADDON_KIND.TRIAL,
				...rowStatus,
			};
		})
		.filter(Boolean)
		.sort((rowA, rowB) => {
			return STATUS_RANK[rowA.status] - STATUS_RANK[rowB.status]
				|| compareCalendarDates(rowA.calendarDate, rowB.calendarDate)
				|| rowA.name.localeCompare(rowB.name);
		});
};

// An expired temporary or trial grant was a courtesy, not a purchase to renew: the pane still lists it as Expired,
// but it raises no icon in the machine list.
const raisesExpiredAlert = (row) => {
	return row.status === VALIDITY_STATUS.EXPIRED && !row.isTemporary && !row.isTrial;
};

// null means "no alert", which covers a machine with healthy licenses and a machine whose licenses are unknown.
// Scheduled and start-on-activation rows never raise one, nor does an expired temporary or trial grant. Each row
// is the grant Central picked for its license, an active one before a lapsed one, so a license with both an expired
// and a valid grant arrives as the valid one and raises nothing.
export const getAddonsAlert = (productAddons, interval) => {
	const rows = getAddonRows(productAddons, interval);

	if (!rows) {
		return null;
	}

	if (rows.some(raisesExpiredAlert)) {
		return VALIDITY_STATUS.EXPIRED;
	}

	const statuses = rows.map((row) => { return row.status; });

	if (statuses.includes(VALIDITY_STATUS.EXPIRING)) {
		return VALIDITY_STATUS.EXPIRING;
	}

	return null;
};
